"use client";

import { Suspense, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { signInAction } from "@/server/signin-action";

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const from = params.get("from") || "/dashboard";
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [pending, startTransition] = useTransition();

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await signInAction(fd);
      if (res.ok) {
        router.push(from);
        router.refresh();
        return;
      }
      if (res.mfaRequired) {
        setMfaRequired(true);
        setError("Enter your 6-digit MFA code to continue.");
        return;
      }
      setError(res.error ?? "Sign in failed.");
    });
  }

  return (
    <form
      onSubmit={onSubmit}
      className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-5"
    >
      <h1 className="text-2xl font-bold text-slate-900">Sign in</h1>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">Email</span>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Password</span>
        <input
          name="password"
          type="password"
          required
          autoComplete="current-password"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
        />
      </label>
      {mfaRequired && (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">MFA code</span>
          <input
            name="totp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none tracking-widest text-center"
          />
        </label>
      )}

      {error && <p className="text-sm text-rose-600">{error}</p>}

      <button
        type="submit"
        disabled={pending}
        className="w-full py-3 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-50 hover:bg-emerald-700"
      >
        {pending ? "Signing in..." : "Sign in"}
      </button>

      <p className="text-sm text-center text-slate-600">
        New here?{" "}
        <Link href="/register" className="text-emerald-700 hover:underline">
          Create an account
        </Link>
      </p>
    </form>
  );
}

export default function LoginPage() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <Suspense>
        <LoginForm />
      </Suspense>
    </main>
  );
}
